/**
 * Temporary storage layer for AppContext
 *
 * This file contains:
 * - Type definitions for Organization
 * - Temporary organization data (until API ready)
 * - LocalStorage keys and utilities
 */

import {
	type Organization,
	OrganizationStatusCode,
} from "@/services/organization";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// export interface Organization {
// 	id: string;
// 	name: string;
// 	initials: string;
// 	// Future: Add more fields as API is implemented
// 	// role?: string;
// 	// permissions?: string[];
// 	// status?: 'active' | 'inactive';
// }

export type AppType = "main" | "admin";

// ============================================================================
// TEMPORARY DATA (Replace with API calls later)
// ============================================================================

/**
 * Temporary organizations - hardcoded until API is ready
 *
 * TODO: Replace with API call to fetch user's organizations
 * Endpoint: GET /api/users/{userId}/organizations
 */
export const TEMP_ORGANIZATIONS: Organization[] = [
	{
		id: "all",
		name: "All Organizations",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
	{
		id: "org-acme",
		name: "Acme Corporation",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
	{
		id: "org-techcorp",
		name: "TechCorp Solutions",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
	{
		id: "org-innovate",
		name: "Innovate Industries",
		statusCode: 200,
		oAuthProviderId: null,
		oAuthProvider: null,
		status: {
			code: OrganizationStatusCode.Active,
			name: "Active",
			description: "The organization is active",
		},
		mfaEnabled: true,
		createdBy: null,
		createdAt: null,
		createdAtOffset: null,
		updatedBy: null,
		updatedAt: null,
		updatedAtOffset: null,
		totalUserCount: 0,
	},
];

// ============================================================================
// LOCALSTORAGE UTILITIES
// ============================================================================

const STORAGE_KEYS = {
	CURRENT_APP: "app-context:current-app",
	CURRENT_ORG: "app-context:current-org-id",
} as const;

/**
 * Get current app from localStorage
 */
export function getStoredApp(): AppType | null {
	if (typeof window === "undefined") return null;

	const stored = localStorage.getItem(STORAGE_KEYS.CURRENT_APP);
	if (stored === "main" || stored === "admin") {
		return stored;
	}
	return null;
}

/**
 * Store current app to localStorage
 */
export function storeApp(app: AppType): void {
	if (typeof window === "undefined") return;
	localStorage.setItem(STORAGE_KEYS.CURRENT_APP, app);
}

/**
 * Get current organization ID from localStorage
 */
export function getStoredOrgId(): string | null {
	if (typeof window === "undefined") return null;
	return localStorage.getItem(STORAGE_KEYS.CURRENT_ORG);
}

/**
 * Store current organization ID to localStorage
 */
export function storeOrgId(orgId: string): void {
	if (typeof window === "undefined") return;
	localStorage.setItem(STORAGE_KEYS.CURRENT_ORG, orgId);
}

/**
 * Clear all stored app context data
 */
export function clearStoredContext(): void {
	if (typeof window === "undefined") return;
	localStorage.removeItem(STORAGE_KEYS.CURRENT_APP);
	localStorage.removeItem(STORAGE_KEYS.CURRENT_ORG);
}

// ============================================================================
// ORGANIZATION UTILITIES
// ============================================================================

/**
 * Find organization by ID
 */
export function findOrganization(orgId: string): Organization | undefined {
	return TEMP_ORGANIZATIONS.find((org) => org.id === orgId);
}

/**
 * Get default organization (first in list)
 */
export function getDefaultOrganization(): Organization {
	return TEMP_ORGANIZATIONS[0];
}

/**
 * Check if user has access to multiple organizations
 */
export function hasMultipleOrganizations(): boolean {
	return TEMP_ORGANIZATIONS.length > 1;
}
